// ── OddsFeedTable — a tennis match's whole OddsPapi feed (#1284) ──────
//
// Takes OddsBooksTable's place on ATP/WTA Game Detail. Tennis is quoted as
// one market today (the match moneyline, capture 2026-10-22), so a table that
// follows the trade panel's line would only ever show that market, capped at
// ten books. This one is an inspection surface: every market the match
// carries, each with EVERY book (quoting or withdrawn) — consensus, then
// Pinnacle, then the field in a fixed order — and every field g1 types per
// quote (American, decimal, fair, update age). Nothing is narrowed for
// presentation; market families g1 drops at ingest are named under the table.
//
// Tour isolation: ATP and WTA share one tour-less OddsPapi key. Only slices
// whose frame names this page's tour render (resolveOddsFeed →
// opSliceMatchesSport); otherwise the table says whose match the key holds.
//
// Vendor limits follow each aligned side and its quote timestamp. Sportsbook
// limits are max stake USD; exchange limits retain the vendor meaning.

const LABEL_REM = 7;
const CONSENSUS_REM = 4.75;
const BOOK_REM = 4.75;

import { useMemo, useState } from 'react';
import { EdgeFadeScroller } from '@/components/cards/cardKit';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { cn } from '@/lib/infra/utils';
import {
  americanForQuote,
  decimalToImpliedPct,
  fairAmericanText,
  fairPctText,
  formatOpAge,
  OP_ANCHOR_GLOW_MS,
  OP_FIELD_FRESH_MS,
  opWithdrawReasonLabel,
  opBookLimitText,
} from '@/lib/opOdds';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useMarketStore, useOpLaneStatus, type OpOddsSlice } from '@/stores/marketStore';
import { GAME_DETAIL_TABLE_FADE } from './CompactLinesTable';
import {
  oddsBoardSides,
  oddsBoardTitle,
  oddsBookLabel,
  oddsFeedBooks,
  oddsFeedBucket,
  oddsFeedOrientation,
  quoteAgeLabel,
  resolveOddsFeed,
} from './oddsBoard';

// ── Freshness cell ──────────────────────────────────────────────────
// The SAME tick rule and classes as OddsBooksTable's TickCell (see its
// header for the windows), with any number of sub-lines: a feed quote shows
// decimal and fair under the American price. A deliberate copy, not an
// extraction: PR #1295 is reworking OddsBooksTable's useTick/TickCell, and
// touching that file here would hand it a merge conflict. Fold the two into
// one shared cell once #1295 lands.

type TickDir = 'up' | 'down' | 'neutral';
/** Literal names, so Tailwind's content scan keeps the @layer utilities. */
const TICK_CLASS: Record<TickDir, string> = {
  up: 'animate-odds-tick-up',
  down: 'animate-odds-tick-down',
  neutral: 'animate-odds-tick-neutral',
};
interface Tick { value: number | null; stamp: number | null; dir: TickDir; delayMs: number }

/** An update seen while mounted starts a full window now; the quote a cell
 *  mounts with starts part-way, by its own age. Derived during render. */
function useTick(value: number | null, stamp: number | null, windowMs: number): Tick {
  const [tick, setTick] = useState<Tick>(() => ({
    value,
    stamp,
    dir: 'neutral',
    delayMs: stamp == null ? 0 : -Math.min(Math.max(0, Date.now() - stamp), windowMs),
  }));
  if (tick.stamp !== stamp) {
    const dir: TickDir =
      value == null || tick.value == null || value === tick.value ? 'neutral' : value > tick.value ? 'up' : 'down';
    const next = { value, stamp, dir, delayMs: 0 };
    setTick(next);
    return next;
  }
  return tick;
}

function ageText(ms: number, now: number): string {
  const s = Math.max(0, Math.round((now - ms) / 1000));
  return s < 60 ? `${s}s ago` : formatOpAge(ms, now);
}

function FeedTickCell({ value, stamp, windowMs, text, sub, title, emphasis = false }: {
  value: number | null;
  stamp: number | null;
  windowMs: number;
  text: string;
  sub: readonly string[];
  title?: string;
  emphasis?: boolean;
}) {
  const tick = useTick(value, stamp, windowMs);
  return (
    <td className="px-0.5 py-0.5 text-center" title={title}>
      <span
        // Remount per quote so the one-shot animation restarts.
        key={stamp ?? 'none'}
        data-tick={stamp != null ? tick.dir : undefined}
        className={cn('inline-block min-w-[3.25rem] rounded px-1 py-0.5 tabular-nums', stamp != null && TICK_CLASS[tick.dir])}
        style={stamp != null ? { animationDuration: `${windowMs}ms`, animationDelay: `${tick.delayMs}ms` } : undefined}
      >
        <span className={cn('block text-xs', emphasis ? 'font-bold' : 'font-semibold')}>{text}</span>
        {sub.map((line, i) => (
          <span key={i} className="text-muted-foreground block text-[10px] leading-tight">{line}</span>
        ))}
      </span>
    </td>
  );
}

interface Props {
  /** The OddsPapi game key (tennis: the tour-less `tennis|…` key). */
  gameKey: string | null;
  /** The page's tour, `atp` or `wta`. */
  sport: string;
  awayCode: string;
  homeCode: string;
}

export function OddsFeedTable({ gameKey, sport, awayCode, homeCode }: Props) {
  const showOpOdds = useDisplaySettings((s) => s.showOpOdds);
  const version = useMarketStore((s) => s.version);
  const lane = useOpLaneStatus(gameKey);
  const { slices, dropped } = useMemo(() => {
    void version;
    const st = useMarketStore.getState();
    return { slices: st.getOpMarketsFor(gameKey), dropped: st.getOpDroppedFamiliesFor(gameKey) };
  }, [version, gameKey]);
  const feed = resolveOddsFeed({ showOpOdds, sport, lane, slices });
  const now = useSharedNow(1000);

  let message: { text: string; tone: 'muted' | 'warn' | 'error' } | null = null;
  switch (feed.kind) {
    case 'hidden': message = { text: 'OddsPapi is hidden by the odds filter.', tone: 'muted' }; break;
    case 'loading': message = { text: 'Loading OddsPapi…', tone: 'muted' }; break;
    case 'waitingForSource': message = { text: 'Waiting for an OddsPapi source — none is connected yet.', tone: 'warn' }; break;
    case 'disconnected': message = { text: 'OddsPapi feed unavailable — no source is connected.', tone: 'error' }; break;
    case 'otherTour': message = {
      // "an ATP", "a WTA" — the letter names (ay-tee-pee, double-u).
      text: `Unavailable — this match's OddsPapi key currently carries ${/^[AEFHILMNORSX]/.test(feed.sport.toUpperCase()) ? 'an' : 'a'} ${feed.sport.toUpperCase()} match, not this ${sport.toUpperCase()} one.`,
      tone: 'error',
    }; break;
    case 'empty': message = { text: 'No OddsPapi markets for this match.', tone: 'muted' }; break;
  }
  const ready = feed.kind === 'ready' ? feed : null;
  const bookCount = ready ? new Set(ready.slices.flatMap((s) => s.market.books.map((b) => b.bookmaker))).size : 0;

  return (
    <section data-testid="odds-feed-table" aria-label="OddsPapi full feed">
      <p className="text-muted-foreground px-3 py-1 text-[10px]">Max $: sportsbook stake in USD at the quoted price. Venue limit: exchange vendor value. Neither is G1 executable size.</p>
      <div className="border-border flex items-center justify-between gap-2 border-b px-3 py-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="text-foreground text-sm font-semibold">OddsPapi</span>
          <span className="text-muted-foreground shrink-0 text-xs">Full feed · {sport.toUpperCase()}</span>
        </div>
        {ready && (
          <span className="text-muted-foreground shrink-0 text-[10px]" data-testid="odds-feed-summary">
            {ready.slices.length} {ready.slices.length === 1 ? 'market' : 'markets'} · {bookCount}{' '}
            {bookCount === 1 ? 'book' : 'books'} quoting
          </span>
        )}
      </div>
      {ready && (ready.disconnected || ready.stale) && (
        <div className="border-border border-b bg-amber-500/10 px-3 py-1 text-[11px] text-amber-300" role="status" data-testid="odds-feed-banner">
          {ready.disconnected
            ? 'OddsPapi feed disconnected — showing the last prices received.'
            : `OddsPapi is stale — last consensus update ${formatOpAge(ready.slices[0].market.consensus?.lastUpdateMs ?? ready.slices[0].lastUpdateMs, now)}.`}
        </div>
      )}
      {message ? (
        <div
          className={cn('px-3 py-4 text-center text-xs', message.tone === 'error' ? 'text-red-400' : message.tone === 'warn' ? 'text-amber-300' : 'text-muted-foreground')}
          role="status"
          data-testid="odds-feed-state"
          data-state={feed.kind}
        >
          {message.text}
        </div>
      ) : (
        // Bounded both ways: wide markets scroll sideways inside their own
        // fade, a long feed scrolls down inside the card.
        <div className="max-h-[32rem] overflow-y-auto" data-testid="odds-feed-markets">
          {ready!.slices.map((slice) => (
            <FeedMarket
              key={`${gameKey}|${sport}|${slice.market.marketType}|${slice.market.period ?? slice.market.periodTypeId}|${slice.market.points ?? ''}|${slice.market.stat ?? ''}|${slice.market.playerKey ?? ''}`}
              gameKey={gameKey ?? ''}
              slice={slice}
              awayCode={awayCode}
              homeCode={homeCode}
              now={now}
            />
          ))}
        </div>
      )}
      {dropped.length > 0 && feed.kind !== 'hidden' && feed.kind !== 'otherTour' && (
        <div className="text-muted-foreground border-border border-t px-3 py-1 text-[10px]" data-testid="odds-feed-dropped">
          Not shown: {dropped.length} market {dropped.length === 1 ? 'family' : 'families'} g1 does not support yet ({dropped.join(', ')}).
        </div>
      )}
    </section>
  );
}

function FeedMarket({ gameKey, slice, awayCode, homeCode, now }: {
  gameKey: string;
  slice: OpOddsSlice;
  awayCode: string;
  homeCode: string;
  now: number;
}) {
  const m = slice.market;
  const names = oddsFeedOrientation(slice, { awayCode, homeCode });
  const title = oddsBoardTitle({ kind: 'bucket', bucket: oddsFeedBucket(gameKey, slice) }, names, slice);
  const sides = oddsBoardSides(slice, names);
  const books = oddsFeedBooks(m);
  const consensus = m.consensus ?? null;
  const withdrawn = new Map((m.withdrawnBooks ?? []).map((w) => [w.bookmaker, w]));
  const updatedAt = new Map(m.books.map((b) => [b.bookmaker, b.updatedAtMs]));
  const caption = [
    m.periodLabel,
    consensus
      ? `consensus of ${consensus.contributingBooks}${consensus.pinnacleAnchored ? ', Pinnacle-anchored' : ''} · ${formatOpAge(consensus.lastUpdateMs, now)}`
      : 'no consensus',
    `${m.books.length} quoting`,
    withdrawn.size > 0 ? `${withdrawn.size} withdrawn` : null,
    m.pairing ? `pairing: ${m.pairing}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="border-border/60 border-b last:border-b-0" data-testid="odds-feed-market" data-market={m.marketType}>
      <div className="flex flex-wrap items-baseline gap-x-2 px-3 pt-2 pb-1">
        <span className="text-foreground text-xs font-semibold">{title}</span>
        <span className="text-muted-foreground text-[10px]">{caption}</span>
      </div>
      <EdgeFadeScroller className={GAME_DETAIL_TABLE_FADE}>
        <table
          className="table-fixed border-collapse border-spacing-0"
          style={{ width: `${LABEL_REM + CONSENSUS_REM + books.length * BOOK_REM}rem` }}
        >
          <caption className="sr-only">{`${title} — ${caption}`}</caption>
          <colgroup>
            <col style={{ width: `${LABEL_REM}rem` }} />
            <col style={{ width: `${CONSENSUS_REM}rem` }} />
            {books.map((b) => <col key={b} style={{ width: `${BOOK_REM}rem` }} />)}
          </colgroup>
          <thead className="bg-muted/50 border-border border-b">
            <tr className="text-muted-foreground text-[10px] font-semibold uppercase">
              <th scope="col" className="px-2 py-1 text-left"><span className="sr-only">Side</span></th>
              <th
                scope="col"
                className="text-foreground px-1 py-1 text-center"
                title="OddsPapi consensus — the de-vigged fair value across the contributing sportsbooks, Pinnacle-weighted when present. Hydra-computed."
              >
                <span className="block">Consensus</span>
                <span className="block text-[9px] font-normal normal-case">(de-vigged)</span>
              </th>
              {books.map((b) => {
                const at = updatedAt.get(b);
                const w = withdrawn.get(b);
                return (
                  <th
                    key={b}
                    scope="col"
                    className="px-1 py-1 text-center align-bottom"
                    title={at != null
                      ? `${oddsBookLabel(b)} — quoted price, with vig. Last update ${ageText(at, now)}.`
                      : w ? `${oddsBookLabel(b)} — withdrawn (${opWithdrawReasonLabel(w.reason)}).` : `${oddsBookLabel(b)} — no quote for this market.`}
                  >
                    <span className="block leading-tight break-words whitespace-normal">{oddsBookLabel(b)}</span>
                    <span className="block text-[9px] font-normal normal-case tabular-nums" data-book-age={b}>
                      {at != null ? quoteAgeLabel(at, now) : '—'}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sides.map((side) => (
              <tr key={side.slot} className="border-border/50 border-b last:border-b-0">
                {/* Wraps, never truncates: a long surname ("Mpetshi Perricard")
                    is data the issue forbids clipping (review #4). */}
                <th scope="row" className="text-foreground px-2 py-1 text-left text-xs leading-tight font-semibold break-words whitespace-normal" title={side.label}>
                  {side.label}
                </th>
                <FeedTickCell
                  value={side.consensusTenths}
                  stamp={side.consensusTenths != null ? consensus?.lastUpdateMs ?? null : null}
                  windowMs={OP_FIELD_FRESH_MS}
                  text={fairPctText(side.consensusTenths) ?? '—'}
                  sub={[fairAmericanText(side.consensusTenths, null)].filter((l): l is string => l != null)}
                  emphasis
                  title={consensus != null ? `Consensus — updated ${ageText(consensus.lastUpdateMs, now)}` : 'No consensus for this market'}
                />
                {books.map((b) => {
                  const q = side.quotes.get(b);
                  const w = withdrawn.get(b);
                  if (!q) {
                    return (
                      <td
                        key={b}
                        className="text-muted-foreground px-1 py-1 text-center text-xs"
                        data-book-cell={b}
                        title={w ? `${oddsBookLabel(b)} withdrawn — ${opWithdrawReasonLabel(w.reason)}, ${formatOpAge(w.sinceMs, now)}` : `${oddsBookLabel(b)} has no quote for this side`}
                      >
                        {w ? (
                          <>
                            <span className="block text-[10px] line-through">{opWithdrawReasonLabel(w.reason)}</span>
                            <span className="block text-[9px]">{formatOpAge(w.sinceMs, now)}</span>
                          </>
                        ) : '—'}
                      </td>
                    );
                  }
                  const american = americanForQuote(q.quote);
                  const fair = fairPctText(q.quote.fairTenths ?? null);
                  return (
                    <FeedTickCell
                      key={b}
                      value={decimalToImpliedPct(q.quote.decimal)}
                      stamp={q.updatedAtMs}
                      windowMs={b === 'pinnacle' ? OP_ANCHOR_GLOW_MS : OP_FIELD_FRESH_MS}
                      text={american}
                      sub={[q.quote.decimal ?? '—', fair != null ? `fair ${fair}` : null, opBookLimitText(b, q.quote.limitMax)].filter((l): l is string => l != null)}
                      title={`${oddsBookLabel(b)} ${american} (decimal ${q.quote.decimal ?? '—'}${fair != null ? `, fair ${fair}` : ''}) — updated ${ageText(q.updatedAtMs, now)}`}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </EdgeFadeScroller>
    </div>
  );
}
